<button type="button" [class]="buttonClass" [disabled]="disabled" (click)="openCloneModal()">
  <i [class]="buttonIcon + ' me-1'"></i>{{ label }}
</button>

<ng-template #cloneModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title mb-0">Clone {{ entityLabel }}</h5>
    <button type="button" class="btn-close btn-close-white" (click)="modal.dismiss()"></button>
  </div>

  <div class="modal-body">
    <div class="d-flex gap-2 mb-2">
      <div class="border rounded p-2 flex-grow-1">
        <div class="text-uppercase text-muted small fw-semibold mb-1" style="letter-spacing:.05em;font-size:.7rem;">
          Copy from
        </div>
        <div class="d-flex gap-2">
          <ng-select class="flex-grow-1" [items]="companyOptions" [bindLabel]="'value'" [bindValue]="'key'"
            appendTo="body" placeholder="Company..." [loading]="isLoadingCompanies"
            [formControl]="sourceCompanyIdControl" (change)="onSourceCompanyChange()"></ng-select>
          <ng-select class="flex-grow-1" [items]="sourceBranchOptions" [bindLabel]="'value'" [bindValue]="'key'"
            appendTo="body" placeholder="Branch..." [disabled]="!sourceBranchOptions.length"
            [formControl]="sourceBranchIdControl" (change)="onSourceBranchChange()"></ng-select>
        </div>
      </div>

      <div class="border rounded p-2 flex-grow-1">
        <div class="text-uppercase text-muted small fw-semibold mb-1" style="letter-spacing:.05em;font-size:.7rem;">
          Copy into
        </div>
        <div class="fw-bold text-nowrap text-truncate" style="font-size:.95rem;">
          <i class="bi bi-building-fill me-1"></i>{{ targetCompanyName }}
          <i class="bi bi-arrow-right-short mx-1"></i>
          <i class="bi bi-signpost-split me-1"></i>
          <span *ngIf="!targetAllBranches" class="text-primary">{{ targetBranchName || '...' }}</span>
          <span *ngIf="targetAllBranches" class="text-primary">All branches of this company ({{ targetBranchOptions.length }})</span>
        </div>
        <div class="form-check mt-1" *ngIf="targetBranchOptions.length > 1">
          <input type="checkbox" class="form-check-input" id="tgtAllBranches" [(ngModel)]="targetAllBranches"
            (change)="onTargetAllBranchesToggle()">
          <label class="form-check-label small" for="tgtAllBranches">Clone into all branches instead</label>
        </div>
      </div>
    </div>

    <div class="alert alert-info py-1 px-2 small mb-2" *ngIf="matchColumn && targetAllBranches">
      <i class="bi bi-info-circle me-1"></i>Rows highlighted in yellow already exist in at least one target branch - hover the badge to see which.
    </div>

    <div *ngIf="sourceReady && singleton">
      <div *ngIf="isLoadingRows" class="text-center text-muted py-4">
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Loading...
      </div>
      <div class="alert alert-secondary py-2 px-3 small mb-0" *ngIf="!isLoadingRows && !rows.length">
        No {{ entityLabel }} found for this branch - nothing to clone.
      </div>
      <div class="alert alert-danger py-2 px-3 small mb-0 d-flex align-items-center gap-2" *ngIf="!isLoadingRows && rows.length">
        <i class="bi bi-exclamation-triangle-fill"></i>
        Found {{ entityLabel }} for this branch. Cloning will overwrite whatever is already set for the target branch(es).
      </div>
    </div>

    <div *ngIf="sourceReady && !singleton">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <input type="text" class="form-control form-control-sm w-auto" style="min-width:220px;"
          placeholder="Filter..." [(ngModel)]="filterText" />
        <span class="badge bg-secondary" *ngIf="selectedKeys.size > 0">{{ selectedKeys.size }} selected</span>
      </div>

      <div class="alert alert-warning py-1 px-2 small mb-2 d-flex align-items-center gap-2" *ngIf="selectedExistingCount > 0">
        <i class="bi bi-exclamation-triangle-fill"></i>
        {{ selectedExistingCount }} of your {{ selectedCount }} selected row(s) already exist in the target and will be skipped - not cloned.
      </div>

      <div *ngIf="isLoadingRows" class="text-center text-muted py-4">
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Loading rows...
      </div>

      <div class="text-center text-muted py-4" *ngIf="!isLoadingRows && !rows.length">
        No active {{ entityLabel }} rows found for this source.
      </div>

      <div class="table-responsive" style="max-height: 45vh; overflow-y: auto;" *ngIf="!isLoadingRows && visibleRows().length">
        <table class="table table-sm table-bordered mb-0">
          <thead>
            <tr>
              <th style="width: 2rem;">
                <input type="checkbox" class="form-check-input" [checked]="isAllSelected" [indeterminate]="isSomeSelected"
                  (change)="toggleSelectAll()" aria-label="Select all rows">
              </th>
              <th *ngFor="let col of columns">{{ col }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of visibleRows()" [class.table-warning]="rowExists(row)">
              <td>
                <input type="checkbox" class="form-check-input" [checked]="isChecked(row)" (change)="toggleRow(row)"
                  [attr.aria-label]="'Select row ' + rowId(row)">
              </td>
              <td *ngFor="let col of columns; let i = index" [title]="row[col]">
                {{ row[col] }}
                <span class="badge bg-warning text-dark ms-1" *ngIf="i === 0 && rowExists(row)"
                  [title]="targetAllBranches ? ('Already exists in: ' + rowExistsBranchNames(row).join(', ')) : ('Already exists in ' + targetBranchName)">
                  {{ targetAllBranches
                      ? ('exists in ' + rowExistsBranchNames(row).length + ' branch' + (rowExistsBranchNames(row).length === 1 ? '' : 'es'))
                      : 'exists in target' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()" [disabled]="isCloning">Cancel</button>
    <button type="button" class="btn btn-primary" [disabled]="selectedCount === 0 || isCloning" (click)="submitClone()">
      <span *ngIf="isCloning" class="spinner-border spinner-border-sm me-2"></span>
      <i class="bi bi-check-lg me-1" *ngIf="!isCloning"></i>
      {{ isCloning ? 'Cloning...' : (singleton ? 'Clone' : 'Clone Selected (' + selectedCount + ')') }}
    </button>
  </div>
</ng-template>
